import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import EntityLogo from "@/components/EntityLogo";
import api from "@/lib/api";
import { normalizeImageUrl } from "@/lib/imageUrl";
import { Building2, Globe, CheckCircle2, Star, Users2, Shield, MapPin, Calendar, Trophy, ChevronRight, Instagram, Twitter, Facebook, Youtube } from "lucide-react";

export default function OrgProfile() {
  const { slug } = useParams();
  const [org, setOrg] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/live/organizations/${slug}`).then(({ data }) => setOrg(data)).catch(() => setError(true));
  }, [slug]);

  if (error) return <PublicLayout><div className="max-w-4xl mx-auto px-6 py-24 text-center text-slate-400">Organization not found.</div></PublicLayout>;
  if (!org) return <PublicLayout><div className="max-w-4xl mx-auto px-6 py-24 text-center text-slate-400">Loading…</div></PublicLayout>;

  const teams = org.teams || [];
  const social = org.social || {};
  const socials = [
    ["instagram", org.instagram || social.instagram, Instagram],
    ["x", org.x_handle || social.x, Twitter],
    ["facebook", org.facebook || social.facebook, Facebook],
    ["youtube", org.youtube || social.youtube, Youtube],
  ].filter(([, v]) => v);

  return (
    <PublicLayout>
      {/* Header */}
      <section className="bg-[#0A192F] text-white" style={org.primary_color ? { background: `linear-gradient(135deg, ${org.primary_color} 0%, #0A192F 70%)` } : {}}>
        <div className="max-w-6xl mx-auto px-6 py-14 flex items-center gap-6 flex-wrap">
          <EntityLogo src={org.logo_url} alt={org.name} size="w-24 h-24" textSize="text-2xl" variant="dark" />
          <div className="flex-1 min-w-[220px]">
            <div className="flex flex-wrap items-center gap-2">
              {org.verified && <span className="text-[9px] font-bold uppercase bg-emerald-500 text-white px-1.5 py-0.5 rounded-sm inline-flex items-center gap-0.5"><CheckCircle2 className="w-2.5 h-2.5" /> Verified</span>}
              {org.founding_member && <span className="text-[9px] font-bold uppercase bg-amber-400 text-[#0A192F] px-1.5 py-0.5 rounded-sm inline-flex items-center gap-0.5"><Star className="w-2.5 h-2.5" /> Founding Member</span>}
              <span className="text-[9px] font-bold uppercase bg-white/15 px-1.5 py-0.5 rounded-sm">{org.type === "cbr_nation" ? "CBR Nation" : org.type}</span>
            </div>
            <h1 data-testid="org-profile-name" className="font-display text-4xl sm:text-5xl uppercase tracking-tighter mt-2">{org.name}</h1>
            <div className="text-sm text-white/70 mt-2 flex flex-wrap items-center gap-3">
              {(org.city || org.province) && <span className="flex items-center gap-1"><MapPin className="w-3.5 h-3.5" /> {[org.city, org.province].filter(Boolean).join(", ")}</span>}
              {org.website && <a href={org.website.startsWith("http") ? org.website : `https://${org.website}`} target="_blank" rel="noreferrer" className="flex items-center gap-1 hover:text-white"><Globe className="w-3.5 h-3.5" /> Website</a>}
              <Link to={`/live/orgs/${slug}/staff`} data-testid="org-profile-staff-link" className="flex items-center gap-1 hover:text-white"><Users2 className="w-3.5 h-3.5" /> Staff Directory</Link>
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-3 gap-8">
        {/* Main: Teams */}
        <div className="lg:col-span-2">
          <h2 className="font-display text-2xl uppercase tracking-tight flex items-center gap-2"><Shield className="w-5 h-5 text-[#E63946]" /> Teams ({teams.length})</h2>
          {teams.length === 0 ? (
            <div data-testid="org-profile-teams-empty" className="mt-4 bg-white border border-dashed border-slate-300 rounded-sm py-12 text-center">
              <Shield className="w-8 h-8 text-slate-300 mx-auto" />
              <p className="text-sm text-slate-500 mt-3">No active teams are linked to this organization yet.</p>
            </div>
          ) : (
            <div className="mt-4 space-y-3" data-testid="org-profile-teams">
              {teams.map((t) => {
                const rating = t.rating || {};
                return (
                  <Link key={t.id} to={`/live/teams/${t.slug}`} data-testid={`org-team-${t.id}`} className="flex items-center gap-3 bg-white border border-slate-200 rounded-sm p-3 hover:border-[#E63946] transition-colors group">
                    <EntityLogo src={t.effective_logo_url} alt={t.name} size="w-11 h-11" textSize="text-[11px]" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold group-hover:text-[#E63946] truncate">{t.name}</div>
                      <div className="text-[11px] text-slate-500">{[t.age_division, t.league, t.province || t.state].filter(Boolean).join(" · ") || "—"}</div>
                    </div>
                    <div className="text-right shrink-0">
                      {rating.cbr_team_rating != null && <div className="font-data text-sm">{rating.cbr_team_rating}</div>}
                      <div className="text-[10px] text-slate-400">
                        {rating.national_rank ? `Nat #${rating.national_rank}` : ""}{rating.national_rank && rating.provincial_rank ? " · " : ""}{rating.provincial_rank ? `Prov #${rating.provincial_rank}` : ""}
                        {t.record?.overall ? `${(rating.national_rank || rating.provincial_rank) ? " · " : ""}${t.record.overall.w || 0}-${t.record.overall.l || 0}` : ""}
                      </div>
                    </div>
                    <ChevronRight className="w-4 h-4 text-[#E63946] shrink-0" />
                  </Link>
                );
              })}
            </div>
          )}
        </div>

        {/* Sidebar: Overview */}
        <div className="space-y-6">
          <div className="bg-white border border-slate-200 rounded-sm p-5" data-testid="org-profile-overview">
            <div className="label-overline text-[#E63946] mb-3">Overview</div>
            {org.description && <p className="text-sm text-slate-600 mb-4">{org.description}</p>}
            <dl className="space-y-2 text-sm">
              {org.founded_year && <Row label="Founded" value={org.founded_year} icon={Calendar} />}
              {org.league_affiliations && <Row label="Leagues" value={org.league_affiliations} icon={Trophy} />}
              {org.home_fields && <Row label="Home Fields" value={org.home_fields} icon={MapPin} />}
            </dl>
            {socials.length > 0 && (
              <div className="mt-4 flex gap-2">
                {socials.map(([k, v, Icon]) => (
                  <a key={k} href={v.startsWith("http") ? v : `https://${v}`} target="_blank" rel="noreferrer" className="w-9 h-9 grid place-items-center border border-slate-200 rounded-sm hover:border-[#E63946] hover:text-[#E63946]"><Icon className="w-4 h-4" /></a>
                ))}
              </div>
            )}
          </div>
          <div className="bg-white border border-slate-200 rounded-sm p-5">
            <div className="label-overline text-[#E63946] mb-2">Staff</div>
            <p className="text-xs text-slate-500">The people responsible for this organization.</p>
            <Link to={`/live/orgs/${slug}/staff`} className="mt-3 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-[#E63946]">View Full Staff Directory <ChevronRight className="w-3.5 h-3.5" /></Link>
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}

function Row({ label, value, icon: Icon }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
      <div><dt className="text-[10px] uppercase tracking-wider text-slate-400">{label}</dt><dd className="text-slate-700">{value}</dd></div>
    </div>
  );
}
